import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Inbox, Users, FileText, BarChart3, Settings, LogOut,
  ShieldCheck, ArrowRight, AlertCircle, Loader2, ExternalLink, Sun, Moon, Menu, X,
} from "lucide-react";
import { accessApi, accessToken } from "@/access/api";
import { useAccessTheme } from "@/access/theme";

const NAV = [
  { to: "/access/dashboard",    label: "Overview",      Icon: LayoutDashboard },
  { to: "/access/leads",        label: "Leads",         Icon: Users           },
  { to: "/access/submissions",  label: "Submissions",   Icon: Inbox           },
  { to: "/access/pages",        label: "Pages & SEO",   Icon: FileText        },
  { to: "/access/analytics",    label: "Analytics",     Icon: BarChart3       },
  { to: "/access/settings",     label: "Settings",      Icon: Settings        },
];

export default function AccessShell() {
  const navigate = useNavigate();
  const loc = useLocation();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, toggle } = useAccessTheme();

  // Force noindex on every /access page
  useEffect(() => {
    const id = "access-robots-meta";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("meta");
      el.id = id; el.name = "robots"; el.content = "noindex, nofollow, noarchive";
      document.head.appendChild(el);
    }
    document.title = "Access · Clinic OS";
    return () => { const n = document.getElementById(id); if (n) n.remove(); };
  }, []);

  useEffect(() => { setSidebarOpen(false); }, [loc.pathname]);

  // Auth check
  useEffect(() => {
    if (!accessToken.get()) { setChecking(false); return; }
    accessApi.me().then(({ data }) => setMe(data))
      .catch(() => { accessToken.clear(); setMe(null); })
      .finally(() => setChecking(false));
  }, []);

  const onLogout = async () => {
    try { await accessApi.logout(); } catch (e) { /* ignore */ }
    accessToken.clear(); setMe(null);
    navigate("/access", { replace: true });
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070710] text-white/70">
        <div className="flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.3em]">
          <Loader2 className="h-3 w-3 animate-spin" /> Verifying access
        </div>
      </div>
    );
  }
  if (!me) return <LoginPane onLoggedIn={setMe} />;

  return (
    <div className="min-h-screen bg-[#070710] text-white">
      {/* Subtle ambient gradient */}
      <div
        className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[420px] opacity-50"
        style={{ background: "radial-gradient(60% 50% at 30% 0%, rgba(124,58,237,0.18), transparent 70%), radial-gradient(50% 50% at 80% 0%, rgba(14,165,233,0.10), transparent 70%)" }}
      />

      <div className="flex min-h-screen">
        {/* ───── LEFT SIDEBAR ───── */}
        {/* Mobile backdrop */}
        {sidebarOpen && (
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          />
        )}

        <aside
          data-testid="access-sidebar"
          className={`fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col border-r border-white/[0.06] bg-[#0a0a14]/95 backdrop-blur-xl transition-transform lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
        >
          {/* Brand */}
          <div className="flex items-center justify-between gap-2 border-b border-white/[0.06] px-5 py-5">
            <Link to="/access/dashboard" className="flex items-center gap-2.5" onClick={() => setSidebarOpen(false)}>
              <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-rose-500 text-white shadow-lg shadow-violet-600/30">
                <ShieldCheck className="h-4 w-4" />
                <span className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-white/20" />
              </span>
              <div>
                <p className="font-display text-[16px] font-black tracking-tight leading-none">Access</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">Clinic OS · admin</p>
              </div>
            </Link>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.03] text-white/70 lg:hidden"
              aria-label="Close menu"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Nav — vertical, bigger touch targets */}
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/35">Workspace</p>
            <div className="space-y-0.5">
              {NAV.map(({ to, label, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`access-nav-${label.toLowerCase().replace(/\W+/g, "-")}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-semibold transition-all ${
                      isActive
                        ? "bg-gradient-to-r from-violet-500/15 via-fuchsia-500/8 to-transparent text-white shadow-inner ring-1 ring-inset ring-violet-400/25"
                        : "text-white/55 hover:bg-white/[0.04] hover:text-white"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={`inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg transition-colors ${
                        isActive ? "bg-violet-500/20 text-violet-200 ring-1 ring-inset ring-violet-400/30" : "bg-white/[0.04] text-white/55 group-hover:bg-white/[0.07] group-hover:text-white"
                      }`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="truncate">{label}</span>
                      {isActive && <ArrowRight className="ml-auto h-3.5 w-3.5 text-violet-300/80" />}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </nav>

          {/* Sidebar footer — user + actions */}
          <div className="border-t border-white/[0.06] px-3 py-4">
            <div className="flex items-center gap-3 rounded-xl bg-white/[0.03] px-3 py-2.5">
              <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-[13px] font-black uppercase text-white shadow-sm">
                {me.email?.[0] || "A"}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-white">{me.email}</p>
                <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/40">Signed in</p>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-1.5">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="access-view-site"
                title="View site"
                className="inline-flex h-9 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.03] text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <button
                type="button"
                onClick={toggle}
                aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                data-testid="access-theme-toggle"
                title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                className="inline-flex h-9 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.03] text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white"
              >
                {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                data-testid="access-logout"
                onClick={onLogout}
                aria-label="Sign out"
                title="Sign out"
                className="inline-flex h-9 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.03] text-white/70 transition-colors hover:border-rose-400/30 hover:bg-rose-500/10 hover:text-rose-200"
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </aside>

        {/* ───── MAIN COLUMN ───── */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Mobile top bar — just the hamburger */}
          <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-white/[0.06] bg-[#070710]/85 px-4 py-3 backdrop-blur-xl lg:hidden">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              data-testid="access-sidebar-toggle"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.03] text-white"
            >
              <Menu className="h-4 w-4" />
            </button>
            <span className="font-display text-[14px] font-black tracking-tight">Access</span>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-[13px] font-black uppercase text-white">
              {me.email?.[0] || "A"}
            </span>
          </header>

          <main data-testid="access-main" className="flex-1 px-3 py-4 sm:px-5 sm:py-6 lg:px-6 lg:py-6">
            <div className="mx-auto max-w-[1400px]">
              <Outlet context={{ me, location: loc }} />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function LoginPane({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const { data } = await accessApi.login(email.trim(), password);
      accessToken.set(data.token);
      onLoggedIn(data.user);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Sign in failed. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#070710] text-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{ background: "radial-gradient(50% 60% at 80% 0%, rgba(124,58,237,0.28), transparent 70%), radial-gradient(45% 55% at 10% 80%, rgba(14,165,233,0.18), transparent 70%)" }}
      />
      <div className="relative mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
        <div className="mb-10 flex items-center gap-3">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-rose-500 text-white shadow-xl shadow-violet-600/30">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <p className="font-display text-xl font-black tracking-tight leading-none">Access</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.28em] text-white/40">Clinic OS · admin</p>
          </div>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl shadow-2xl shadow-violet-950/40">
          <h1 className="font-display text-2xl font-black tracking-tight sm:text-3xl">Sign in</h1>
          <p className="mt-2 text-[13px] text-white/55">Restricted — not indexed.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="access-login-form">
            <label className="block">
              <span className="mb-2 inline-block text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/55">Email</span>
              <input
                type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@clinicfirst.app" data-testid="access-input-email"
                className="block w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-white/25 focus:border-violet-400/60 focus:bg-white/[0.05]"
              />
            </label>
            <label className="block">
              <span className="mb-2 inline-block text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/55">Password</span>
              <input
                type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="access-input-password"
                className="block w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-white/25 focus:border-violet-400/60 focus:bg-white/[0.05]"
              />
            </label>
            {error && (
              <p className="inline-flex items-start gap-1.5 text-[13px] font-semibold text-rose-300">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-none" />
                <span>{error}</span>
              </p>
            )}
            <button
              type="submit" disabled={busy} data-testid="access-submit"
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 px-5 text-sm font-bold text-white shadow-lg shadow-violet-600/30 transition-all hover:-translate-y-0.5 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? "Signing in…" : "Sign in"}
              {!busy && <ArrowRight className="h-3.5 w-3.5" />}
            </button>
          </form>
        </div>
        <p className="mt-6 text-center text-[11px] text-white/40">
          For internal use only · Clinic OS &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
